import { useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { ConfirmPopover } from '@ui/components/ConfirmPopover/ConfirmPopover';
import { useConfirmPopover } from '@ui/components/ConfirmPopover/useConfirmPopover';
import { DateView } from '@ui/components/DateView';
import { InlineEditButton } from '@ui/components/InlineEditButton/InlineEditButton';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { ThemeButton } from '@ui/components/ThemeButton';
import { RemoveIcon } from '@ui/icons/RemoveIcon';

import { ClubNewsItemEditForm } from '@features/club/ui/ClubNewsItemEditForm/ClubNewsItemEditForm';
import { NewsPost } from '@features/shared/api/typings';
import { ThemeIconButton } from '@features/shared/ui/ThemeIconButton';

import styles from './ClubNewsListEdit.module.css';

type Props = {
  className?: string;
  news?: NewsPost[];
  onNewsSave: (newsPost: NewsPost) => Promise<unknown>;
  onNewsDelete: (newsPostId: string) => void;
  refetch: () => Promise<unknown>;
};
export function ClubNewsListEdit({ className, news, onNewsSave, onNewsDelete, refetch }: Props) {
  const [editNewsPostId, setEditNewsPostId] = useState<string | 'new' | null>(null);

  const handleNewsEdit = useCallback(
    (newsPost: NewsPost) => {
      setEditNewsPostId(newsPost.id);
    },
    [setEditNewsPostId],
  );
  const handleAdd = useCallback(() => {
    setEditNewsPostId('new');
  }, [setEditNewsPostId]);
  const handleEditCancel = useCallback(() => {
    setEditNewsPostId(null);
  }, [setEditNewsPostId]);

  const handleSubmit = useCallback(
    async (newsPost: NewsPost) => {
      await onNewsSave(newsPost);
      await refetch();
      setEditNewsPostId(null);
    },
    [onNewsSave, refetch, setEditNewsPostId],
  );

  const handleDelete = useCallback(
    async (newsPost: NewsPost) => {
      await onNewsDelete(newsPost.id);
      await refetch();
    },
    [onNewsDelete, refetch],
  );

  const editingNewsItem = news?.find((newsItem) => newsItem.id === editNewsPostId);
  if (editNewsPostId) {
    return (
      <ClubNewsItemEditForm
        initialValue={editingNewsItem}
        onSubmit={handleSubmit}
        onCancel={handleEditCancel}
      />
    );
  }

  return (
    <div className={cx(className, styles.root)}>
      <div className={styles.buttons}>
        <ThemeButton onClick={handleAdd}>Добавить новость</ThemeButton>
      </div>

      {!news || news.length < 1 ? (
        <div className={styles.placeholder}>Нет новостей</div>
      ) : (
        <Table className={styles.newsList}>
          {news.map((newsPost) => (
            <NewsItem
              key={newsPost.id}
              newsPost={newsPost}
              onEdit={handleNewsEdit}
              onDelete={handleDelete}
            />
          ))}
        </Table>
      )}
    </div>
  );
}

type NewsItemProps = {
  newsPost: NewsPost;
  onDelete: (newsPost: NewsPost) => void;
  onEdit: (newsPost: NewsPost) => void;
};

function NewsItem({ newsPost, onDelete, onEdit }: NewsItemProps) {
  const handleDelete = useCallback(() => {
    onDelete(newsPost);
  }, [onDelete, newsPost]);

  const handleEdit = useCallback(() => {
    onEdit(newsPost);
  }, [onEdit, newsPost]);

  const confirmPopover = useConfirmPopover();

  return (
    <TableRow>
      <TableCell className={styles.title}>{newsPost.title}</TableCell>
      <TableCell slim leftGap className={styles.promo}>
        {newsPost.promoted && 'Промо'}
      </TableCell>
      <TableCell slim leftGap>
        <DateView showTime shortMonth date={newsPost.publishTimestampMs} />
      </TableCell>
      <TableCell slim leftGap>
        <InlineEditButton onClick={handleEdit} />
      </TableCell>
      <TableCell ref={confirmPopover.anchorRef} top slim leftGap>
        <ThemeIconButton onClick={confirmPopover.open}>
          <RemoveIcon />
        </ThemeIconButton>
        <ConfirmPopover
          description="Удалить новость?"
          onConfirm={handleDelete}
          {...confirmPopover.props}
        />
      </TableCell>
    </TableRow>
  );
}
